import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { Student, TeacherSchool } from '@/context/OverviewContext';
import { useSelectedStudent } from '@/context/SelectedStudentContext';

/** Scroll only when there are more than four schools (Figma: max visible ≈4 rows). */
const SCROLL_WHEN_MORE_THAN = 4;

interface SchoolListProps {
  schools: TeacherSchool[];
  /** Synthetic preview students aligned by index with `schools` (same order). */
  previewStudents: Student[];
  /** Shows centered loading spinner (Figma loading state). */
  loading?: boolean;
  onSelect?: (school: TeacherSchool, student: Student) => void;
}

const SchoolList: React.FC<SchoolListProps> = ({ schools, previewStudents, loading = false, onSelect }) => {
  const { selectedStudent, setSelectedStudent } = useSelectedStudent();
  const navigate = useNavigate();

  const handleSchoolClick = (school: TeacherSchool, index: number) => {
    const student = previewStudents[index];
    if (!student) return;
    setSelectedStudent(student);
    onSelect?.(school, student);
    navigate('/overview');
  };

  if (loading) {
    return (
      <div className="student-loading-spinner school-list-loading" aria-busy="true" aria-label="Loading schools">
        <svg className="spinner" viewBox="0 0 50 50" data-testid="school-list-spinner">
          <circle className="spinner-bg" cx="25" cy="25" r="20" fill="none" strokeWidth="5" />
          <circle className="spinner-fg" cx="25" cy="25" r="20" fill="none" strokeWidth="5" />
        </svg>
      </div>
    );
  }

  const needsScroll = schools.length > SCROLL_WHEN_MORE_THAN;
  const listClassName = ['student-list', 'school-list', needsScroll ? 'school-list--scrollable' : 'school-list--fit']
    .join(' ')
    .trim();

  return (
    <div className={listClassName}>
      {schools.map((school, index) => {
        const preview = previewStudents[index];
        const isActive = preview && selectedStudent?.accountId === preview.accountId;

        return (
          <div
            key={school.schoolId}
            className={`student-row school-row ${isActive ? 'active' : ''}`}
            onClick={() => handleSchoolClick(school, index)}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSchoolClick(school, index);
              }
            }}
          >
            <div className="student-info">
              <div className="student-details">
                <div className="student-name">{school.schoolName}</div>
              </div>
            </div>
            <svg
              className="student-arrow-icon"
              width="25"
              height="25"
              viewBox="0 0 25 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
            >
              <path
                d="M5.68555 12.1858H19.6855M19.6855 12.1858L12.6855 5.18579M19.6855 12.1858L12.6855 19.1858"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        );
      })}
    </div>
  );
};

export default SchoolList;
